import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReadinessReadResponse } from '@formency/shared';
import { api } from '../client';

const READINESS_QUERY_KEY = ['me', 'readiness'] as const;

/**
 * Fetch the user's current Learner Readiness Score (with recovery curve applied
 * server-side per spec §6.7 and §10.4).
 *
 * Refetch policy (algo v1.8.0):
 *  - `staleTime: 60 * 1000` — match the polling interval so an alt-tab refocus
 *    inside the same minute is a free no-op rather than triggering a redundant
 *    fetch on top of the regular interval.
 *  - `refetchInterval: 60 * 1000` — background refetch every minute. The
 *    response now includes `activeMinutesInEpisode` and `episodeStartedAtMs`,
 *    which drive the session-player tooltip; minute-cadence polling matches
 *    the display granularity (whole minutes) and the server-side step_time
 *    flush cadence (~60s).
 *  - `refetchOnWindowFocus: true` (default) — handles cross-tab freshness
 *    on returns after `staleTime` has elapsed.
 *
 * Endpoint is cheap (cached readiness lookup + indexed episode aggregation),
 * so the extra cadence is fine. See README §19 v1.0.35 for the v1.8.0 rationale.
 *
 * Manual invalidation triggers (call `useInvalidateReadiness` in those handlers):
 *  - successful POST /sessions/:id/complete
 *  - successful POST /sessions/:id/abandon
 *  - client-side navigation away from the session player ("implicit pause")
 */
export function useMyReadiness() {
  return useQuery<ReadinessReadResponse>({
    queryKey: READINESS_QUERY_KEY,
    queryFn: () => api.get<ReadinessReadResponse>('/me/readiness'),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

/**
 * Helper to invalidate the readiness query — call from session-end handlers
 * (complete/abandon/navigate-away) so the dashboard reflects the user's
 * post-session state immediately rather than waiting for the next refetch tick.
 */
export function useInvalidateReadiness() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: READINESS_QUERY_KEY });
}
